import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { CurrencyInr, Trophy, ChartBar, FileText, FileCsv, FileXls } from "@phosphor-icons/react";

const FIELDS = [
  ["interview_scheduled", "Interview Scheduled", "Paid to executive when an interview is scheduled"],
  ["interview_confirmed", "Interview Confirmed", "Extra payout when the candidate confirms the interview"],
  ["weekly_top_performer", "Weekly Top Performer", "Awarded weekly to the #1 executive"],
  ["monthly_team_leader", "Monthly Best Manager", "Awarded to the top manager every month"],
  ["placement_bonus", "Placement Bonus", "One-time bonus once the candidate is placed"],
  ["special_campaign_bonus", "Special Campaign Bonus", "Ad-hoc campaigns"],
];

const PERIODS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
  { key: "annual", label: "Annual" },
];

function ConfigTab() {
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/incentives").then((r) => setConfig(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map(([k]) => [k, Number(config[k] || 0)]));
      payload.currency = config.currency || "INR";
      const r = await api.put("/incentives", payload);
      setConfig(r.data);
      toast.success("Incentives saved");
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="incentives-config-tab">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 rounded-md bg-emerald-600 text-white grid place-items-center">
          <Trophy size={18} />
        </div>
        <div>
          <div className="font-semibold">Payout structure</div>
          <div className="text-xs text-slate-500">Currency</div>
        </div>
        <select value={config.currency || "INR"} onChange={(e) => setConfig((c) => ({ ...c, currency: e.target.value }))}
                className="ml-auto border border-slate-200 rounded-md px-3 py-2 text-sm">
          <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option>
        </select>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map(([k, label, desc]) => (
          <div key={k} className="p-3 border border-slate-100 rounded-md">
            <label className="text-xs uppercase tracking-widest text-slate-500 font-medium">{label}</label>
            <div className="text-xs text-slate-400 mb-2">{desc}</div>
            <div className="relative">
              <CurrencyInr size={14} className="absolute left-3 top-2.5 text-slate-400" />
              <input type="number" value={config[k] ?? ""}
                     onChange={(e) => setConfig((c) => ({ ...c, [k]: e.target.value }))}
                     data-testid={`incentive-${k}`}
                     className="w-full border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <button onClick={save} disabled={saving} data-testid="save-incentives"
                className="bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-sm px-4 py-2 rounded-md">
          {saving ? "Saving…" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
}

function ReportTab() {
  const [period, setPeriod] = useState("monthly");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ rows: [], currency: "INR" });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/incentive-reports?period=${period}&format=json`);
      setData(data);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail));
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const download = async (format) => {
    try {
      const res = await api.get(`/incentive-reports?period=${period}&format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      const ext = format === "excel" ? "xlsx" : format;
      a.download = `incentives_${period}.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const filtered = data.rows.filter((r) => !q || r.employee?.toLowerCase().includes(q.toLowerCase()) || r.team?.toLowerCase().includes(q.toLowerCase()));
  const total = filtered.reduce((s, r) => s + (r.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="incentives-report-tab">
      <div className="bg-white border border-slate-200 rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-slate-500">Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="incentive-period"
                  className="block border border-slate-200 rounded-md px-3 py-2 text-sm">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-slate-500">Search</label>
          <input placeholder="Employee or team…" value={q} onChange={(e) => setQ(e.target.value)}
                 data-testid="incentive-search"
                 className="block border border-slate-200 rounded-md px-3 py-2 text-sm" />
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={() => download("pdf")} data-testid="incentive-dl-pdf" className="text-xs border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1"><FileText size={12} /> PDF</button>
          <button onClick={() => download("excel")} data-testid="incentive-dl-excel" className="text-xs border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1"><FileXls size={12} /> Excel</button>
          <button onClick={() => download("csv")} data-testid="incentive-dl-csv" className="text-xs border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2 inline-flex items-center gap-1"><FileCsv size={12} /> CSV</button>
          <button onClick={() => window.print()} data-testid="incentive-print" className="text-xs border border-slate-200 hover:bg-slate-50 rounded-md px-3 py-2">Print</button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">Rank</th>
              <th className="text-left px-3 py-2">Employee</th>
              <th className="text-left px-3 py-2">Team</th>
              <th className="text-right px-3 py-2">Interviews</th>
              <th className="text-right px-3 py-2">Interview ₹</th>
              <th className="text-right px-3 py-2">Confirmed</th>
              <th className="text-right px-3 py-2">Confirmed ₹</th>
              <th className="text-right px-3 py-2">Placements</th>
              <th className="text-right px-3 py-2">Placement ₹</th>
              <th className="text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-6 text-slate-400">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-slate-400">No rows.</td></tr>}
            {filtered.map((r) => (
              <tr key={r.employee} className="border-t border-slate-100" data-testid={`incentive-row-${r.rank}`}>
                <td className="px-3 py-2">{r.rank}</td>
                <td className="px-3 py-2 font-medium">{r.employee}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.team}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.interviews}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.interview_amount.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.confirmed_interviews}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.confirmed_amount.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.placements}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.placement_amount.toFixed(2)}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-700">{r.total.toFixed(2)}</td>
              </tr>
            ))}
            {filtered.length > 0 && (
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold">
                <td colSpan={9} className="px-3 py-2 text-right">Total {data.currency}</td>
                <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{total.toFixed(2)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const TABS = [
  { key: "config", label: "Configuration", icon: Trophy },
  { key: "report", label: "Incentive Report", icon: ChartBar },
];

export default function Incentives() {
  const [tab, setTab] = useState("config");
  return (
    <div className="space-y-5" data-testid="incentives-page">
      <div>
        <h2 className="text-2xl font-semibold">Incentives</h2>
        <p className="text-sm text-slate-500">Configure payouts and generate detailed incentive reports.</p>
      </div>
      <div className="border-b border-slate-200 flex gap-2">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-incentives-${t.key}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1 ${tab === t.key ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "config" ? <ConfigTab /> : <ReportTab />}
    </div>
  );
}
